import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ritornoDa } from "@/lib/ritorno";
import { supabase } from "@/lib/supabase";
import { useAuth, MFA } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthShell, { Avviso, TestataModulo } from "@/components/auth/AuthShell";
import { CampoPassword } from "@/components/auth/CampiAccesso";

/* Cosa dire quando l'accesso non va: la password sbagliata e la rete che non risponde non si risolvono
   alla stessa maniera. */
const messaggioDi = (error) =>
  error.status === 400 || error.code === "invalid_credentials"
    ? "Email o password non corrette. Controlla le maiuscole e riprova."
    : error.status === 429 || error.code === "over_request_rate_limit"
      ? "Troppi tentativi in poco tempo. Aspetta un minuto e riprova."
      : "Non riesco a collegarmi. Controlla la connessione e riprova fra un momento.";

export default function LoginPage() {
  const nav = useNavigate();
  const ritorno = ritornoDa(useLocation().search); // da dove è arrivata la persona (la pagina di consenso di Claude)
  const { session, profile, factors, aal, mfaStato, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    if (loading || !session) return;
    // Finché il secondo fattore non si sa, non si decide: `{ totp: [] }` vuol dire «non lo
    // sappiamo», non «non ce l'ha». Se non arriva, il cancello di «/» dice cosa fare.
    if (mfaStato === MFA.NON_DISPONIBILE) {
      nav("/", { replace: true });
      return;
    }
    if (mfaStato !== MFA.DISPONIBILE) return;
    const hasTotp = (factors?.totp || []).some((f) => f.status === "verified");
    if (profile?.ruolo === "titolare" && !hasTotp) {
      nav("/auth/totp-enrollment", { replace: true });
    } else if (hasTotp && aal?.currentLevel === "aal1" && aal?.nextLevel === "aal2") {
      nav(ritorno ? `/auth/totp-verify?next=${encodeURIComponent(ritorno)}` : "/auth/totp-verify", { replace: true });
    } else if (profile) {
      nav(ritorno || "/", { replace: true }); // la porta «/» rimanda all'arrivo del ruolo (lib/accessi.js)
    }
  }, [session, profile, factors, aal, mfaStato, loading, nav, ritorno]);

  const submit = async (e) => {
    e.preventDefault();
    setErrore(null);
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) setErrore(messaggioDi(error));
  };

  return (
    <AuthShell testId="login-pagina">
      <form data-testid="login-form" onSubmit={submit} className="flex flex-col gap-[18px]" autoComplete="on">
        <TestataModulo titolo="Accedi">Con la tua email aziendale e la tua password.</TestataModulo>

        {errore ? <Avviso data-testid="login-errore">{errore}</Avviso> : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email" className="font-semibold text-ink">Email aziendale</Label>
          <Input
            id="email"
            type="email"
            required
            autoFocus
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            placeholder="nome.cognome@internidautore.com"
            aria-invalid={errore ? true : undefined}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password" className="font-semibold text-ink">Password</Label>
          <CampoPassword
            id="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            testId="login-password-input"
            invalido={Boolean(errore)}
          />
        </div>

        <Button type="submit" size="xl" loading={submitting} loadingText="Accesso in corso…" className="w-full max-md:h-[54px]" data-testid="login-submit-btn">
          Accedi al gestionale
        </Button>

        <div className="flex flex-col gap-1 text-ink-2">
          <p>Accesso riservato al personale. Per avere un account, chiedi alla direzione.</p>
          <Link to="/aiuto/entrare" className="inline-flex min-h-11 items-center font-semibold text-brand-ink underline underline-offset-[3px]" target="_blank" rel="noopener noreferrer" data-testid="aiuto-link">
            Come si entra, passo per passo
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
